/**
 * Solicitor View Page (`/share/:shareId`)
 *
 * Public, no-auth route surfaced to a solicitor by a seller's share link.
 * Read-only: progress percent, per-section confirmed/total counts, the
 * seller's first name, and a "Not legal advice" disclaimer. NO PDF
 * download. NO field edits. NO document uploads.
 *
 * Visual design notes (Feature 37 design constraint):
 * - Does NOT render the standard {@link Header} — solicitors see this in
 *   isolation; the global nav would invite them to start their own
 *   pack, which is not the intended call to action.
 * - Minimal own-header: site name as plain text + "Not legal advice"
 *   strip.
 * - Single column, max 760px (narrower than the standard 1100px) to keep
 *   the page feeling like a status report rather than a marketing page.
 * - Single emerald CTA at the bottom: B2B lead-gen, addressed to the
 *   solicitor (the only person reading this page).
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { ShareLinkPublicPayload } from '@property-intel/shared';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; payload: ShareLinkPublicPayload }
  | { kind: 'not-found' }
  | { kind: 'rate-limited' }
  | { kind: 'error'; message: string };

export default function SolicitorView() {
  const { shareId } = useParams<{ shareId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!shareId) {
      setState({ kind: 'not-found' });
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/share/${shareId}`, {
          method: 'GET',
          // No credentials — public endpoint. Sending cookies would be
          // harmless but pointless.
          headers: { 'Content-Type': 'application/json' },
        });
        if (cancelled) return;
        if (response.status === 404) {
          setState({ kind: 'not-found' });
          return;
        }
        if (response.status === 429) {
          setState({ kind: 'rate-limited' });
          return;
        }
        if (!response.ok) {
          setState({ kind: 'error', message: 'Unable to load this share link.' });
          return;
        }
        const payload = (await response.json()) as ShareLinkPublicPayload;
        setState({ kind: 'ready', payload });
      } catch {
        if (!cancelled) {
          setState({ kind: 'error', message: 'Unable to load this share link.' });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  return (
    <>
      <Helmet>
        <title>Seller's pack progress — UKPropertyChecks</title>
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href="https://ukpropertychecks.co.uk/" />
      </Helmet>
      <div className="min-h-screen bg-white text-slate-900">
        <SolicitorHeader />
        <main
          id="main-content"
          className="mx-auto max-w-[760px] px-4 sm:px-6 py-10"
          aria-labelledby="solicitor-view-title"
        >
          {state.kind === 'loading' && <LoadingState />}
          {state.kind === 'not-found' && <NotFoundState />}
          {state.kind === 'rate-limited' && <RateLimitedState />}
          {state.kind === 'error' && <ErrorState message={state.message} />}
          {state.kind === 'ready' && <ReadyView payload={state.payload} />}
        </main>
        <SolicitorFooter />
      </div>
    </>
  );
}

function SolicitorHeader() {
  return (
    <header
      role="banner"
      className="border-b border-slate-200 bg-white"
    >
      <div className="mx-auto max-w-[760px] px-4 sm:px-6 py-4 flex flex-col gap-1">
        <p className="text-base font-semibold text-slate-950">UKPropertyChecks</p>
        <p className="text-sm text-slate-700">
          Read-only seller's pack progress. Not legal advice.
        </p>
      </div>
    </header>
  );
}

function SolicitorFooter() {
  return (
    <footer
      role="contentinfo"
      className="border-t border-slate-200 bg-white mt-12"
    >
      <div className="mx-auto max-w-[760px] px-4 sm:px-6 py-6 text-sm text-slate-700 space-y-2">
        <p>
          UKPropertyChecks helps sellers prepare TA6/TA7/LPE1 disclosure packs.
          This page shows the current state of one seller's pack — nothing else.
        </p>
        <p>
          <Link to="/" className="text-blue-700">
            About UKPropertyChecks
          </Link>
        </p>
      </div>
    </footer>
  );
}

function LoadingState() {
  return (
    <section aria-busy="true">
      <h1 id="solicitor-view-title" className="text-2xl font-semibold text-slate-950">
        Loading…
      </h1>
      <p className="mt-3 text-slate-700">Fetching the latest pack status.</p>
    </section>
  );
}

function NotFoundState() {
  return (
    <section>
      <h1 id="solicitor-view-title" className="text-2xl font-semibold text-slate-950">
        This share link is no longer available
      </h1>
      <p className="mt-4 text-slate-800">
        The seller may have revoked access, or the link may have expired.
        Ask the seller for an up-to-date link if you still need to see their pack progress.
      </p>
    </section>
  );
}

function RateLimitedState() {
  return (
    <section>
      <h1 id="solicitor-view-title" className="text-2xl font-semibold text-slate-950">
        Too many requests
      </h1>
      <p className="mt-4 text-slate-800">
        This share link has received too many requests in the last minute.
        Please wait a moment and refresh.
      </p>
    </section>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <section>
      <h1 id="solicitor-view-title" className="text-2xl font-semibold text-slate-950">
        Something went wrong
      </h1>
      <p className="mt-4 text-slate-800">{message}</p>
    </section>
  );
}

function ReadyView({ payload }: { payload: ShareLinkPublicPayload }) {
  const { ownerFirstName, progress, sectionsProgress, disclaimer } = payload;
  const headline = `${ownerFirstName} is preparing their seller's pack`;

  // Group sections by formType for display.
  const groups: Record<string, typeof sectionsProgress> = {};
  for (const section of sectionsProgress) {
    if (!groups[section.formType]) {
      groups[section.formType] = [];
    }
    groups[section.formType].push(section);
  }
  const formOrder: Array<'TA6' | 'TA7' | 'LPE1'> = ['TA6', 'TA7', 'LPE1'];
  const formLabels: Record<string, string> = {
    TA6: 'TA6 — Property Information',
    TA7: 'TA7 — Leasehold Information',
    LPE1: 'LPE1 — Leasehold Property Enquiries',
  };

  return (
    <section className="space-y-8">
      <header>
        <h1 id="solicitor-view-title" className="text-2xl sm:text-3xl font-semibold text-slate-950">
          {headline}
        </h1>
        <p className="mt-2 text-slate-700">
          You are viewing a read-only progress summary shared with you by the seller.
        </p>
      </header>

      {/* Overall progress */}
      <section
        className="rounded-lg border border-slate-200 bg-white p-6"
        aria-labelledby="overall-progress-heading"
      >
        <h2 id="overall-progress-heading" className="text-lg font-semibold text-slate-950">
          Overall progress
        </h2>
        <p className="mt-2 text-sm text-slate-700">
          {progress.confirmed} of {progress.total} required answers confirmed
        </p>
        <div className="mt-4">
          <div
            role="progressbar"
            aria-valuenow={progress.percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Pack completion percentage"
            className="h-3 w-full rounded-full bg-slate-100 overflow-hidden"
          >
            <div
              className="h-full bg-emerald-700"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <p className="mt-2 text-2xl font-semibold text-slate-950">
            {progress.percentage}%
          </p>
        </div>
      </section>

      {/* Per-form sections */}
      {formOrder.map((formType) => {
        const sections = groups[formType];
        if (!sections || sections.length === 0) return null;
        return (
          <section
            key={formType}
            className="rounded-lg border border-slate-200 bg-white p-6"
            aria-labelledby={`form-${formType}-heading`}
          >
            <h2
              id={`form-${formType}-heading`}
              className="text-lg font-semibold text-slate-950"
            >
              {formLabels[formType]}
            </h2>
            <ul className="mt-4 divide-y divide-slate-200">
              {sections.map((section) => (
                <li
                  key={`${formType}-${section.section}`}
                  className="py-3 flex justify-between items-center"
                >
                  <span className="text-slate-900">{section.section}</span>
                  <span className="text-sm text-slate-700">
                    {section.confirmed} / {section.total}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {/* Disclaimer */}
      <aside
        className="rounded-lg border-l-4 border-amber-400 bg-amber-50 p-4 text-sm text-slate-900"
        role="note"
      >
        {disclaimer}
      </aside>

      {/* B2B CTA — addressed to the solicitor reading this */}
      <section
        className="rounded-lg border border-slate-200 bg-slate-50 p-6"
        aria-labelledby="solicitor-cta-heading"
      >
        <h2 id="solicitor-cta-heading" className="text-lg font-semibold text-slate-950">
          Receive solicitor-ready packs from your other clients
        </h2>
        <p className="mt-2 text-slate-700">
          If you would like other selling clients to send you their disclosure packs in this
          format, get in touch.
        </p>
        <div className="mt-4">
          <Link to="/contact" className="btn-primary no-underline">
            Contact UKPropertyChecks
          </Link>
        </div>
      </section>
    </section>
  );
}
